import React, { useEffect, useMemo } from "react";
// ANT DESIGN COMPONENTS
import { Select, Tooltip, Button, message } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// COPY TO CLIPBOARD
import { CopyToClipboard } from "react-copy-to-clipboard";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector, useDispatch } from "react-redux";
import { updateFilters } from "../../../redux/reports/reportsSlice";
// CUSTOM HOOKS
import useGetAllServersLight from "../../../customHooks/GET_requests/useGetAllServersLight";
// INTERFACES
import { ServerLightInterface } from "../../../interfaces/Server.interface";
import { ReportSliceFiltersInterface } from "../../../interfaces/Reports.interface";
import { RootState } from "../../../app/store";

function ReportsFilterGetServers() {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const { Option, OptGroup } = Select;
  const dispatch = useDispatch();
  const [t] = useTranslation("global");

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { filters, blockFilters } = useSelector(
    (state: RootState) => state.report
  );

  // ************************************************ */
  // CUSTOM HOOKS *********************************** */
  const { serversLight, isLoadingGetAllServersLight } = useGetAllServersLight();

  // ************************************************ */
  // GROUPED DATA *********************************** */
  const organizationsGroups = useMemo(() => {
    const orgMap = new Map<string, { id: string; name: string }>();
    serversLight.forEach((server: ServerLightInterface) => {
      if (server.organization && !orgMap.has(server.organization.id)) {
        orgMap.set(server.organization.id, server.organization);
      }
    });
    return Array.from(orgMap.values());
  }, [serversLight]);

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const onChangeServer = (newValue: string) => {
    const copy: ReportSliceFiltersInterface = structuredClone(filters);
    const server = serversLight.find(
      (s: ServerLightInterface) => s.id === newValue
    );
    if (server) {
      copy.serverId = server.id;
      copy.serverName = server.name;
      if (server.organization) {
        copy.organizationId = server.organization.id;
        copy.organizationName = server.organization.name;
      }
    }
    dispatch(updateFilters(copy));
  };

  // ************************************************ */
  // USE EFFECT ************************************* */
  useEffect(() => {
    if (serversLight.length > 0 && filters.serverId === "") {
      const sorted = [...serversLight].sort(
        (a: ServerLightInterface, b: ServerLightInterface) =>
          a.name.localeCompare(b.name)
      );
      onChangeServer(sorted[0].id);
    }
  }, [serversLight]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div className="generalStyles__width100" style={{ display: 'flex', alignItems: 'center' }}>
      <Button size="small" disabled style={{ minWidth: 'fit-content' }}>
        {t("report.selectServer")}
      </Button>
      <Tooltip
        title={serversLight.length === 0 ? t("report.noServers") : ""}
      >
        <Select
          className="generalStyles__width100"
          disabled={serversLight.length === 0 || blockFilters}
          showSearch
          optionFilterProp="label"
          size="small"
          listHeight={450}
          placeholder={t("report.selectServer")}
          value={filters.serverId === "" ? null : filters.serverId}
          onChange={onChangeServer}
          loading={isLoadingGetAllServersLight}
        >
          {organizationsGroups.map((org) => (
            <OptGroup key={org.id} label={`ORGANIZATION / ${org.name}`}>
              {serversLight
                .filter(
                  (server: ServerLightInterface) =>
                    server.organization?.id === org.id
                )
                .sort((a: ServerLightInterface, b: ServerLightInterface) =>
                  a.name.localeCompare(b.name)
                )
                .map((server: ServerLightInterface) => (
                  <Option key={server.id} value={server.id} label={server.name}>
                    <Tooltip
                      overlayInnerStyle={{ minWidth: '380px' }}
                      placement="right"
                      title={
                        <div>
                          <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Server</div>
                          <div><b className="generalStyles__info">{server.name}</b></div>
                          <div style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', marginBottom: '6px', paddingBottom: '6px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span><b className="generalStyles__info">ID:</b> <span style={{ fontSize: '11px', opacity: 0.85 }}>{server.id}</span></span>
                              <CopyToClipboard text={server.id} onCopy={() => message.success('Server ID copied')}>
                                <FontAwesomeIcon icon={faCopy} style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '11px', opacity: 0.7 }} />
                              </CopyToClipboard>
                            </div>
                          </div>
                          <div style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 600 }}>Organization</div>
                          <div><b className="generalStyles__info">{server.organization?.name}</b></div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span><b className="generalStyles__info">ID:</b> <span style={{ fontSize: '11px', opacity: 0.85 }}>{server.organization?.id}</span></span>
                            <CopyToClipboard text={server.organization?.id || ''} onCopy={() => message.success('Organization ID copied')}>
                              <FontAwesomeIcon icon={faCopy} style={{ cursor: 'pointer', marginLeft: '8px', fontSize: '11px', opacity: 0.7 }} />
                            </CopyToClipboard>
                          </div>
                        </div>
                      }
                    >
                      {server.name}
                    </Tooltip>
                  </Option>
                ))}
            </OptGroup>
          ))}
        </Select>
      </Tooltip>
    </div>
  );
}

export default ReportsFilterGetServers;
